import { useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, Bookmark, ChevronRight, Gift, Info, Lock, Mail, Smile, Ticket, Trophy } from 'lucide-react'
import { punten } from '../lib/opmaak'
import type { Beloning, Voucher } from '../lib/typen'

/*
 * Wat de klant ziet na het scannen, zoals de tablet van het oude systeem:
 * links het saldo en een menu, rechts de inhoud, onderaan "Klaar".
 * Inwisselen gaat altijd via de PIN van een medewerker (Tablet.tsx).
 */

export interface Profiel { naam: string; voornaam: string; achternaam: string; email: string | null; heeftEmail: boolean; toestemming: boolean; wasbeurten: number; sinds: string }
export interface Regel { soort: string; punten: number; t: string; vestiging: string; beloning: string }
export interface Klantstand {
  status: 'bijgeschreven' | 'wacht' | 'ingewisseld' | 'geactiveerd' | 'bekeken'
  code?: string; saldo: number; beloningen: Beloning[]; punten?: number; minuten?: number
  bezocht?: number; totaal?: number; chauffeurBereikt?: boolean; beloning?: string; medewerker?: string; emailBestaatAl?: boolean
  rang?: { naam: string; afbeelding: string | null; kleur: string | null } | null
  vouchers?: Voucher[]
  profiel?: Profiel
  geschiedenis?: Regel[]
}

type Tab = 'beloningen' | 'vouchers' | 'profiel' | 'nieuwsbrief' | 'geschiedenis'

const TABS: { k: Tab; naam: string; icoon: ReactNode }[] = [
  { k: 'beloningen', naam: 'Beloningen', icoon: <Gift size={24} /> },
  { k: 'vouchers', naam: 'Vouchers', icoon: <Ticket size={24} /> },
  { k: 'profiel', naam: 'Profiel', icoon: <Smile size={24} /> },
  { k: 'nieuwsbrief', naam: 'Nieuwsbrief', icoon: <Mail size={24} /> },
  { k: 'geschiedenis', naam: 'Geschiedenis', icoon: <Bookmark size={24} /> },
]

const SOORT: Record<string, string> = {
  scan: 'Wasbeurt', inwisseling: 'Ingewisseld', verjaardag: 'Verjaardag', correctie: 'Correctie',
  import: 'Overgezet uit het oude systeem', samengevoegd: 'Samengevoegd', activering: 'Nieuwe kaart',
}

const datumTijd = (iso: string) => new Date(iso).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' })

export function Klantscherm({ u, geblokkeerd, feest, kies, kiesVoucher, klaar, sec, tik, nieuwsbrief, naamOpslaan }: {
  u: Klantstand; geblokkeerd: boolean; feest: boolean
  kies: (b: Beloning) => void; kiesVoucher: (v: Voucher) => void; klaar: () => void
  sec: number; tik: number; nieuwsbrief: (aan: boolean) => Promise<void>
  naamOpslaan: (voornaam: string, achternaam: string) => Promise<void>
}) {
  const [tab, zetTab] = useState<Tab>('beloningen')
  const ingewisseld = u.status === 'ingewisseld'
  const vouchers = u.vouchers ?? []
  return (
    <div className="ks">
      <aside className="ks-zij">
        <div className="ks-balans">
          <small>Balans</small>
          <b className="cijfer">{u.saldo}</b>
          <Chip u={u} />
          {u.rang && <span className="ks-rang">{u.rang.naam}</span>}
        </div>
        <nav className="ks-menu">
          {TABS.map((t) => (
            <button key={t.k} className="ks-tab" aria-current={tab === t.k ? 'page' : undefined} onClick={() => zetTab(t.k)}>
              {t.icoon}<span>{t.naam}</span>
              {t.k === 'vouchers' && vouchers.length > 0 && <span className="ks-teller">{vouchers.length}</span>}
              {tab !== t.k && <ChevronRight size={22} className="ks-pijl" />}
            </button>
          ))}
        </nav>
        <div className="ks-onder">
          <div className="ks-aftellen" aria-hidden><i key={tik} style={{ animationDuration: `${sec}s` }} /></div>
          <button className="ks-klaar" onClick={klaar}>Klaar</button>
        </div>
      </aside>

      <main className="ks-inhoud">
        {feest && (
          <div className="ks-melding ok"><Trophy size={24} /><span><b>Chauffeursbeloning gehaald!</b> Je was op {u.bezocht} van de {u.totaal} vestigingen. We nemen contact met je op.</span></div>
        )}
        {u.emailBestaatAl && (
          <div className="ks-melding"><Info size={22} /><span>Je e-mailadres had al punten op een ander account. Deze kaart spaart apart; vraag aan de balie of we ze samenvoegen.</span></div>
        )}
        {ingewisseld && (
          <div className="ks-melding ok"><Gift size={22} /><span><b>{u.beloning}</b> ingewisseld. Bevestigd door {u.medewerker}.</span></div>
        )}
        {geblokkeerd && (tab === 'beloningen' || tab === 'vouchers') && (
          <div className="ks-melding fout"><Lock size={22} /><span>Inwisselen staat tijdelijk uit. Een manager geeft het vrij in Beheer.</span></div>
        )}

        {tab === 'beloningen' && (
          u.beloningen.length ? u.beloningen.map((b) => {
            const kan = u.saldo >= b.punten && !geblokkeerd && !ingewisseld && !b.uitverkocht
            return (
              <button key={b.id} className="ks-kaart" disabled={!kan} onClick={() => kies(b)}>
                {b.afbeelding ? <img className="ks-foto" src={b.afbeelding} alt="" /> : <span className="ks-foto leeg"><Gift size={28} /></span>}
                <span className="ks-tekst">
                  <b>{b.naam}</b>
                  <small>{b.uitverkocht ? 'Uitverkocht' : kan ? 'Tik om in te wisselen' : u.saldo >= b.punten ? '' : `Nog ${punten(b.punten - u.saldo)}`}{b.type === 'digitaal' ? ' · digitaal' : ''}</small>
                </span>
                <Ring punten={b.punten} saldo={u.saldo} op={!!b.uitverkocht} />
              </button>
            )
          }) : <Leeg tekst="Geen beloningen op deze vestiging" />
        )}

        {tab === 'vouchers' && (
          vouchers.length ? vouchers.map((v) => (
            <button key={v.id} className="ks-kaart" disabled={geblokkeerd || ingewisseld} onClick={() => kiesVoucher(v)}>
              {v.afbeelding ? <img className="ks-foto" src={v.afbeelding} alt="" /> : <span className="ks-foto leeg"><Ticket size={28} /></span>}
              <span className="ks-tekst">
                <b>{v.naam}</b>
                <small>Gratis{v.verloopt ? ` · geldig t/m ${new Date(v.verloopt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}` : ''}</small>
              </span>
              <span className="ks-gebruik">Gebruik</span>
            </button>
          )) : <Leeg tekst="Geen vouchers beschikbaar" />
        )}

        {tab === 'profiel' && (
          <div className="ks-velden">
            <Naam p={u.profiel} opslaan={naamOpslaan} />
            <Veld k="E-mail" v={u.profiel?.email ?? 'niet ingevuld'} />
            <Veld k="Kaart" v={<span className="ks-code">{u.code}</span>} />
            <Veld k="Niveau" v={u.rang?.naam ?? '—'} />
            <Veld k="Wasbeurten" v={String(u.profiel?.wasbeurten ?? 0)} />
            {u.totaal ? <Veld k="Vestigingen dit jaar" v={`${u.bezocht ?? 0} van ${u.totaal}`} /> : null}
            {u.profiel?.sinds && <Veld k="Klant sinds" v={new Date(u.profiel.sinds).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })} />}
            <p className="ks-uitleg">Je verjaardag of e-mailadres wijzigen? Dat doe je op je telefoon: tw1club.com, met de code van je kaart.</p>
          </div>
        )}

        {tab === 'nieuwsbrief' && <Nieuwsbrief p={u.profiel} wissel={nieuwsbrief} />}

        {tab === 'geschiedenis' && (
          u.geschiedenis?.length ? (
            <ul className="ks-tijdlijn">
              {u.geschiedenis.map((g, i) => (
                <li key={i} className={g.punten < 0 ? 'af' : ''}>
                  <b>{g.punten > 0 ? '+' : '−'}{punten(Math.abs(g.punten))}{g.beloning ? ` · ${g.beloning}` : ''}</b>
                  <span>{g.vestiging ? `Truckwash 1 ${g.vestiging.replace(/^Truckwash\s*\d*\s*/i, '')}` : SOORT[g.soort] ?? g.soort}</span>
                  <small>{datumTijd(g.t)}</small>
                </li>
              ))}
            </ul>
          ) : <Leeg tekst="Nog geen geschiedenis" />
        )}
      </main>
    </div>
  )
}

function Chip({ u }: { u: Klantstand }) {
  if (u.status === 'bijgeschreven' || u.status === 'geactiveerd') {
    return <span className="ks-chip op"><ArrowUp size={22} strokeWidth={3} />+{u.punten ?? 0}</span>
  }
  if (u.status === 'ingewisseld' && u.punten) return <span className="ks-chip af"><ArrowDown size={22} strokeWidth={3} />−{u.punten}</span>
  if (u.status === 'wacht') return <span className="ks-chip wacht">Al gespaard · weer over {u.minuten} min</span>
  return null
}

/** De voortgangsring met de prijs in punten: vol en groen als je hem kunt halen. */
function Ring({ punten: p, saldo, op }: { punten: number; saldo: number; op: boolean }) {
  const deel = op ? 0 : Math.min(1, p > 0 ? saldo / p : 1)
  const r = 26
  const omtrek = 2 * Math.PI * r
  return (
    <span className={`ks-ring ${deel >= 1 ? 'vol' : ''} ${op ? 'op' : ''}`}>
      <svg viewBox="0 0 64 64" aria-hidden>
        <circle cx="32" cy="32" r={r} className="spoor" />
        <circle cx="32" cy="32" r={r} className="boog" strokeDasharray={omtrek} strokeDashoffset={omtrek * (1 - deel)} transform="rotate(-90 32 32)" />
      </svg>
      <b>{p}</b>
    </span>
  )
}

/** Voor- en achternaam invullen. De achternaam mag leeg; in een aanhef gebruiken we alleen de voornaam. */
function Naam({ p, opslaan }: { p?: Profiel; opslaan: (voornaam: string, achternaam: string) => Promise<void> }) {
  const [voornaam, zetVoornaam] = useState(p?.voornaam ?? '')
  const [achternaam, zetAchternaam] = useState(p?.achternaam ?? '')
  const [bezig, zetBezig] = useState(false)
  const [uit, zetUit] = useState<{ tekst: string; goed: boolean } | null>(null)
  const gewijzigd = voornaam.trim() !== (p?.voornaam ?? '') || achternaam.trim() !== (p?.achternaam ?? '')
  return (
    <form className="ks-naam" onSubmit={async (e) => {
      e.preventDefault()
      if (!voornaam.trim()) { zetUit({ tekst: 'Vul je voornaam in.', goed: false }); return }
      zetBezig(true); zetUit(null)
      try { await opslaan(voornaam.trim(), achternaam.trim()); zetUit({ tekst: 'Opgeslagen.', goed: true }) }
      catch (x) { zetUit({ tekst: x instanceof Error ? x.message : 'Dat lukte niet.', goed: false }) }
      finally { zetBezig(false) }
    }}>
      <label className="ks-invoer"><small>Voornaam</small>
        <input value={voornaam} onChange={(e) => { zetVoornaam(e.target.value); zetUit(null) }} maxLength={40} autoComplete="off" autoCorrect="off" placeholder="Voornaam" enterKeyHint="next" />
      </label>
      <label className="ks-invoer"><small>Achternaam <span>(mag leeg)</span></small>
        <input value={achternaam} onChange={(e) => { zetAchternaam(e.target.value); zetUit(null) }} maxLength={40} autoComplete="off" autoCorrect="off" placeholder="Achternaam" enterKeyHint="done" />
      </label>
      <div className="ks-naam-onder">
        <button className="ks-opslaan" disabled={bezig || !gewijzigd}>{bezig ? 'Even geduld…' : 'Opslaan'}</button>
        {uit && <span className={uit.goed ? 'goed' : 'fout'}>{uit.tekst}</span>}
      </div>
    </form>
  )
}

function Veld({ k, v }: { k: string; v: ReactNode }) {
  return <div className="ks-veld"><small>{k}</small><span>{v}</span></div>
}

function Leeg({ tekst }: { tekst: string }) {
  return <div className="ks-leeg"><span className="ks-i">i</span>{tekst}</div>
}

function Nieuwsbrief({ p, wissel }: { p?: Profiel; wissel: (aan: boolean) => Promise<void> }) {
  const [bezig, zetBezig] = useState(false)
  const [fout, zetFout] = useState<string | null>(null)
  const aan = !!p?.toestemming
  return (
    <div className="ks-velden">
      <div className="ks-schakel">
        <b>Aanbiedingen en nieuws van Truckwash 1 Group</b>
        <span className={aan ? 'aan' : 'uit'}>{aan ? 'Ingeschreven' : 'Uitgeschreven'}</span>
        <button role="switch" aria-checked={aan} className="ks-schakelaar" disabled={bezig || (!aan && !p?.heeftEmail)}
          aria-label="Nieuwsbrief aan of uit" onClick={async () => {
            zetBezig(true); zetFout(null)
            try { await wissel(!aan) } catch (e) { zetFout(e instanceof Error ? e.message : 'Dat lukte niet.') } finally { zetBezig(false) }
          }}><i /></button>
      </div>
      {!p?.heeftEmail && <p className="ks-uitleg">Er staat nog geen e-mailadres bij deze kaart. Vul het in op je telefoon (tw1club.com), dan kun je je hier aanmelden.</p>}
      {fout && <div className="ks-melding fout"><span>{fout}</span></div>}
      <p className="ks-uitleg">Afmelden kan altijd, hier of met de link onder elke mail.</p>
    </div>
  )
}
